import { EventBus, EVENT_BUS } from './eventBus.js';

export const DropdownModule = (function () {
  // Cache DOM elements
  const DOM = {
    burgerButton: document.querySelector('.button-burger'),
    headerDropdown: document.querySelector('.header-dropdown'),
    menuItems: document.querySelectorAll('.accordeon-link'),
    leftMenu: document.querySelector('.content.toggleable-content'),
    anchorsMenu: document.querySelector('.anchors'),
    footer: document.querySelector('footer'),
  };

  // Module state
  const STATE = {
    isLeftMenuOpened: false,
    isResizing: false,
  };

  const toggleHeaderDropdown = () => {
    const isDropdownOpen = DOM.headerDropdown.classList.contains(
      'header-dropdown-opened',
    );
    isDropdownOpen ? closeHeaderDropdown() : openHeaderDropdown();
  };

  const openHeaderDropdown = () => {
    DOM.headerDropdown.classList.add('header-dropdown-opened');
    DOM.burgerButton.classList.add('active');
    EventBus.emit(EVENT_BUS.EVENTS.HEADER_MENU.OPEN);
  };

  const closeHeaderDropdown = () => {
    DOM.headerDropdown.classList.remove('header-dropdown-opened');
    DOM.burgerButton.classList.remove('active');
    EventBus.emit(EVENT_BUS.EVENTS.HEADER_MENU.CLOSE);
  };

  const handleBurgerClick = (event) => {
    event.stopPropagation();
    toggleHeaderDropdown();
  };

  const handleDocumentClick = (event) => {
    const isClickOutsideMenu =
      !DOM.headerDropdown.contains(event.target) &&
      !DOM.burgerButton.contains(event.target);

    if (
      isClickOutsideMenu &&
      DOM.headerDropdown.classList.contains('header-dropdown-opened')
    ) {
      closeHeaderDropdown();
    }
  };

  const initializeAccordeonBehavior = () => {
    const checkboxes = document.querySelectorAll('.accordeon-input');
    checkboxes?.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const accordeon = checkbox.closest('.accordeon');
        if (accordeon) {
          checkbox.checked
            ? accordeon.classList.add('opened')
            : accordeon.classList.remove('opened');
        }
      });
    });
  };

  const toggleLeftMenuVisibility = () => {
    if (STATE.isLeftMenuOpened) {
      DOM.leftMenu.classList.remove('opened');
      DOM.leftMenu.classList.add('closed');
    } else {
      DOM.leftMenu.classList.add('opened');
      DOM.leftMenu.classList.remove('closed');
    }
    STATE.isLeftMenuOpened = !STATE.isLeftMenuOpened;
  };

  const initializeLeftMenu = () => {
    if (!DOM.leftMenu) return;

    const isMenuClosed = localStorage.getItem('isLeftMenuClosed') === 'true';
    DOM.leftMenu.classList.remove('opened', 'closed');
    DOM.leftMenu.classList.add(isMenuClosed ? 'closed' : 'opened');
    STATE.isLeftMenuOpened = !isMenuClosed;

    const toggleButton = DOM.leftMenu.querySelector('.button-toggle');
    toggleButton?.addEventListener('click', (event) => {
      event.preventDefault();
      toggleLeftMenuVisibility();
      localStorage.setItem(
        'isLeftMenuClosed',
        DOM.leftMenu.classList.contains('closed'),
      );
    });
  };

  const setActiveMenuItem = () => {
    const currentUrl = window.location.pathname;
    const links = document.querySelectorAll('.accordeon-link');

    links.forEach((link) => {
      if (link.getAttribute('href') === currentUrl) {
        link.classList.add('current');

        const parentWrapper = link.closest('.accordeon-wrapper');
        if (parentWrapper) {
          parentWrapper.classList.add('current');
        }

        let parent = link.closest('.accordeon');
        while (parent) {
          parent.classList.add('opened');
          parent = parent.parentElement?.closest('.accordeon');
        }

        const submenu = link
          .closest('.accordeon')
          ?.querySelector('.accordeon-content');
        if (submenu) {
          submenu.classList.add('opened');
        }

        try {
          if (link.scrollIntoViewIfNeeded) {
            link.scrollIntoViewIfNeeded();
          } else {
            link.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }
        } catch (error) {
          console.warn('Error scrolling to active menu item:', error);
        }
      }
    });
  };

  const handleMenuItemClick = (event) => {
    const target = event.currentTarget;
    const parent = target?.closest('.accordeon');

    if (!parent) return;

    const hasNestedAccordeon = parent.querySelector('.accordeon-content');
    const isParentLink =
      hasNestedAccordeon && target === parent.querySelector('.accordeon-link');
    const isCurrent = target.classList.contains('current');

    if (isParentLink && isCurrent) {
      event.preventDefault();
      parent.classList.toggle('opened');
      return;
    }

    if (parent && !isCurrent) {
      parent.classList.add('opened');
    }

    if (isCurrent) {
      event.preventDefault();
    }
  };

  const updateMenusHeight = () => {
    if (!DOM.leftMenu || !DOM.footer) return;

    const footerRect = DOM.footer.getBoundingClientRect();
    const headerHeight = document.querySelector('header')?.offsetHeight || 0;
    let height = window.innerHeight - headerHeight;

    if (footerRect.top < window.innerHeight) {
      height = footerRect.top - headerHeight;
    }

    height = Math.max(height, 0);
    DOM.leftMenu.style.height = `${height}px`;

    if (DOM.anchorsMenu) {
      DOM.anchorsMenu.style.height = `${height}px`;
    }

    const shouldFillHeight = footerRect.top < window.innerHeight;
    DOM.leftMenu.classList.toggle('fill-height', shouldFillHeight);
    DOM.anchorsMenu?.classList.toggle('fill-height', shouldFillHeight);
  };

  const initializeMenusHeight = () => {
    if (!DOM.leftMenu || !DOM.footer) return;

    updateMenusHeight();

    const observer = new IntersectionObserver(() => updateMenusHeight(), {
      threshold: [0.8],
      rootMargin: '0px',
    });

    observer.observe(DOM.footer);

    let resizeTimeout;
    const handleViewportChange = () => {
      if (STATE.isResizing) return;
      updateMenusHeight();
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateMenusHeight, 50);
    };

    window.addEventListener('scroll', handleViewportChange);
    window.addEventListener('resize', handleViewportChange);
  };

  const initialize = () => {
    try {
      initializeLeftMenu();
      initializeMenusHeight();

      DOM.burgerButton.addEventListener('click', handleBurgerClick);
      document.addEventListener('click', handleDocumentClick);

      DOM.menuItems.forEach((item) => {
        item.addEventListener('click', handleMenuItemClick);
      });

      setActiveMenuItem();
      initializeAccordeonBehavior();

      EventBus.on(EVENT_BUS.EVENTS.RESIZER.START, () => {
        STATE.isResizing = true;
      });

      EventBus.on(EVENT_BUS.EVENTS.RESIZER.END, () => {
        STATE.isResizing = false;
      });
    } catch (error) {
      console.error('Error initializing menu module:', error);
    }
  };

  return {
    init: initialize,
    toggleLeftMenu: toggleLeftMenuVisibility,
  };
})();
